import { useSelector } from "react-redux";
import { IsLoading } from "src/common/components/AppState/IsLoading";
import {
  addMedia,
  getElevenlabsVoices,
  getPreferredElevenLabsVoice,
  mediaSelector,
  resetTtsPreview,
  uploadAvatar,
} from "../state/gallerySlice";
import { useTranslation } from "react-i18next";
import { Dropdown } from "src/common/components/Input";
import { MediaNameInput } from "src/common/components/Input/MediaNameInput";
import { useEffect, useRef, useState } from "react";
import { loadingSelector, LoadingState } from "src/common/state/slice/appStateSlice";
import { RootState } from "src/common/state/store";
import { GENERATE_MEDIA_PANEL_COMPONENT } from "./GenerateMediaPanel";
import { useAppDispatch } from "src/common/state/hooks";
import { getCurrentModalStateSlider } from "src/common/state/slice/modal/modalSlice";
import { isStringNullOrEmpty } from "src/common/util/isStringNullOrEmpty";
import Spinner from "src/common/components/Loader/Spinner";
import { IMedia } from "src/model/unityObject";

export const CreateAvatar = ({ closeSliderAnimation }: { closeSliderAnimation(): any }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const mapElevenlabsVoices = () => {
    return elevenLabsVoices.map((voice) => {
      const localizedVoiceGender = t(`general.${voice.gender}`);
      return {
        ...voice,
        value: voice.id,
        optionText: `${voice.name} (${localizedVoiceGender})`,
        gender: voice.gender,
      };
    });
  };

  const openSliderId = useSelector(getCurrentModalStateSlider);
  const elevenLabsVoices = useSelector(getElevenlabsVoices);

  const { loading } = useSelector((state: RootState) => {
    return {
      loading: loadingSelector(state, GENERATE_MEDIA_PANEL_COMPONENT),
    };
  });

  const [mediaNameToEdit, setMediaNameToEdit] = useState<string>("");
  const [tagEditorKey, setTagEditorKey] = useState(0);
  // const [voiceOptions, setVoiceOptions] = useState(mapElevenlabsVoices());
  const fetchedVoices = mapElevenlabsVoices();
  const [voiceOptionsFR, setVoiceOptionsFR] = useState(
    fetchedVoices.filter((elt) => elt.language === "fr" || elt.tts_provider === "elevenLabs"),
  );
  const [voiceOptionsEN, setVoiceOptionsEN] = useState(
    fetchedVoices.filter((elt) => elt.language === "en" || elt.tts_provider === "elevenLabs"),
  );
  const [voiceOptionsES, setVoiceOptionsES] = useState(
    fetchedVoices.filter((elt) => elt.language === "es" || elt.tts_provider === "elevenLabs"),
  );
  const defaultTtsVoice = useSelector(getPreferredElevenLabsVoice);
  const medias = useSelector(mediaSelector);

  type VoiceSelection = {
    voiceId: string;
    ttsProvider: string;
    name?: string;
    gender?: string;
  };

  const [voiceFr, setVoiceFr] = useState<VoiceSelection>(defaultTtsVoice);
  const [voiceEn, setVoiceEn] = useState<VoiceSelection>(defaultTtsVoice);
  const [voiceEs, setVoiceEs] = useState<VoiceSelection>(defaultTtsVoice);
  const [nameUnique, setNameUnique] = useState(true);
  const [currentAvatarUrl, setCurrentAvatarUrl] = useState<string>("");
  const [isImgPreviewLoading, setIsImgPreviewLoading] = useState<boolean>(false);

  const avatarIframeRef = useRef();
  const avatarImgPreviewRef = useRef();

  if (avatarImgPreviewRef?.current) {
    (avatarImgPreviewRef.current as any).onload = () => {
      setIsImgPreviewLoading(false);
    };
  }

  const resetAvatar = () => {
    setMediaNameToEdit("");
    dispatch(resetTtsPreview());
    setTagEditorKey(tagEditorKey + 1);

    setVoiceFr(defaultTtsVoice);
    setVoiceEn(defaultTtsVoice);
    setVoiceEs(defaultTtsVoice);

    setCurrentAvatarUrl("");

    const fetchedVoices = mapElevenlabsVoices();
    // setVoiceOptions(fetchedVoices.filter((elt) => elt.tts_provider === "elevenLabs"));
    setVoiceOptionsFR(
      fetchedVoices.filter((elt) => elt.language === "fr" || elt.tts_provider === "elevenLabs"),
    );
    setVoiceOptionsEN(
      fetchedVoices.filter((elt) => elt.language === "en" || elt.tts_provider === "elevenLabs"),
    );
    setVoiceOptionsES(
      fetchedVoices.filter((elt) => elt.language === "es" || elt.tts_provider === "elevenLabs"),
    );
  };

  useEffect(() => {
    resetAvatar();
  }, [openSliderId]);

  const parseEvent = (event) => {
    try {
      return JSON.parse(event.data);
    } catch (error) {
      return null;
    }
  };

  useEffect(() => {
    const receiveMessage = (event) => {
      const res = parseEvent(event);
      if (!res || res?.source !== "readyplayerme") {
        return;
      }
      const frame = avatarIframeRef.current as any;

      switch (res.eventName) {
        case "v1.frame.ready":
          frame.contentWindow.postMessage(
            JSON.stringify({
              target: "readyplayerme",
              type: "subscribe",
              eventName: "v1.**",
            }),
            "*",
          );
          break;
        case "v1.avatar.exported":
          if (
            res.data.url.startsWith("https://") &&
            res.data.url.endsWith(".glb") &&
            res.data.url !== currentAvatarUrl
          ) {
            const glbUrl = res.data.url;
            setCurrentAvatarUrl(glbUrl);

            setIsImgPreviewLoading(true);
            const imgLink = res.data.url.replace(".glb", ".png");
            (avatarImgPreviewRef.current as any).src = imgLink;
          }
          break;
      }
    };

    window.addEventListener("message", receiveMessage);
    return () => {
      window.removeEventListener("message", receiveMessage);
    };
  }, [currentAvatarUrl, dispatch]);

  const saveAvatar = async () => {
    const nameNotAvailable = medias.find((m: IMedia) => m.name === mediaNameToEdit);

    if (nameNotAvailable) {
      setNameUnique(false);
      return;
    } else {
      setNameUnique(true);
    }

    await dispatch(
      uploadAvatar({
        name: mediaNameToEdit,
        avatarUrl: currentAvatarUrl,
        voices: [
          {
            voiceId: voiceFr.voiceId,
            lang: "fr",
            gender: voiceFr.gender || "unknown",
            prettyVoiceName: voiceFr.name,
            tts_provider: voiceFr.ttsProvider,
          },
          {
            voiceId: voiceEn.voiceId,
            lang: "en",
            gender: voiceEn.gender || "unknown",
            prettyVoiceName: voiceEn.name,
            tts_provider: voiceEn.ttsProvider,
          },
          {
            voiceId: voiceEs.voiceId,
            lang: "es",
            gender: voiceEs.gender || "unknown",
            prettyVoiceName: voiceEs.name,
            tts_provider: voiceEs.ttsProvider,
          },
        ],
        componentId: GENERATE_MEDIA_PANEL_COMPONENT,
      }),
    ).then((res) => {
      if (res?.payload) {
        dispatch(addMedia(res.payload));
      }
      closeSliderAnimation();
    });
  };

  return (
    <div className="h-[calc(90vh-2rem-0.75rem-48px)]">
      <IsLoading
        componentId={GENERATE_MEDIA_PANEL_COMPONENT}
        spinnerPlaceholder
        showSuccess={false}
        spinnerStyle={{ marginTop: "4vh" }}
      >
        <div className="w-full h-3/4 flex justify-center">
          {(!currentAvatarUrl || currentAvatarUrl === "") && (
            <iframe
              id="inlineFrameExample"
              title="Avatar Creator"
              width="100%"
              height="100%"
              src="https://wixar.readyplayer.me/avatar?frameApi"
              allow="clipboard-write"
              ref={avatarIframeRef}
            ></iframe>
          )}
          {isImgPreviewLoading && <Spinner />}
          <img
            ref={avatarImgPreviewRef}
            id="avatar-gen-panel-img-preview"
            className={`max-w-full max-h-full ${
              currentAvatarUrl && currentAvatarUrl !== "" ? "" : "hidden"
            }`}
            src={""}
          ></img>
        </div>

        <div className="h-1/4">
          <div>
            <div>
              <div className="block font-medium text-gray-700 ">{t("general.fileName")}</div>
              <MediaNameInput
                value={mediaNameToEdit}
                onChange={setMediaNameToEdit}
                maxLength={55}
                disabled={loading === LoadingState.Loading}
              />
              <div className={`text-red-600 ${nameUnique ? "invisible" : ""}`}>
                {t("general.mediaNameAlreadyUsed")}
              </div>
            </div>

            <div className="flex w-full justify-between">
              <Dropdown
                options={voiceOptionsFR}
                label={t("pages.gallery.generateVoicePanel.voice") + " FR"}
                labelFontClassName={"text-md mt-2"}
                onChangeCb={(_value: string, index: number) => {
                  setVoiceFr({
                    voiceId: voiceOptionsFR[index].id,
                    ttsProvider: voiceOptionsFR[index].tts_provider,
                    name: voiceOptionsFR[index].name,
                    gender: voiceOptionsFR[index].gender,
                  });
                }}
                asDefaultValue
                defaultValueIndex={
                  voiceOptionsFR.findIndex((option) => option.value === voiceFr.voiceId) || 0
                }
              />

              <Dropdown
                options={voiceOptionsEN}
                label={t("pages.gallery.generateVoicePanel.voice") + " EN"}
                labelFontClassName={"text-md mt-2"}
                onChangeCb={(_value: string, index: number) => {
                  setVoiceEn({
                    voiceId: voiceOptionsEN[index].id,
                    ttsProvider: voiceOptionsEN[index].tts_provider,
                    name: voiceOptionsEN[index].name,
                    gender: voiceOptionsEN[index].gender,
                  });
                }}
                asDefaultValue
                defaultValueIndex={
                  voiceOptionsEN.findIndex((option) => option.value === voiceEn.voiceId) || 0
                }
              />

              <Dropdown
                options={voiceOptionsES}
                label={t("pages.gallery.generateVoicePanel.voice") + " ES"}
                labelFontClassName={"text-md mt-2"}
                onChangeCb={(_value: string, index: number) => {
                  setVoiceEs({
                    voiceId: voiceOptionsES[index].id,
                    ttsProvider: voiceOptionsES[index].tts_provider,
                    name: voiceOptionsES[index].name,
                    gender: voiceOptionsES[index].gender,
                  });
                }}
                asDefaultValue
                defaultValueIndex={
                  voiceOptionsES.findIndex((option) => option.value === voiceEs.voiceId) || 0
                }
              />
            </div>
            <div className="mt-2">
              <button
                disabled={
                  isStringNullOrEmpty(currentAvatarUrl) ||
                  isStringNullOrEmpty(mediaNameToEdit) ||
                  mediaNameToEdit.length > 55 ||
                  mediaNameToEdit.trim() === ""
                }
                className={"btn-primary-fill mr-3 mt-2 disabled:cursor-not-allowed"}
                onClick={saveAvatar}
              >
                {t("general.save")}
              </button>
            </div>
          </div>
        </div>
      </IsLoading>
    </div>
  );
};
